import type { ReactNode } from 'react';
import { Bookmark, SquareKanban, type LucideIcon } from 'lucide-react';
import type { TaskSearchHit } from '@shared/types/tasks';
import { formatTaskNumber } from '@shared/task-number';
import {
  buildSavedFilterHref,
  SavedFilterNormalizationError,
} from '@shared/saved-filter-query';
import { searchSavedFilters, searchTasks, type SavedFilterSearchItem } from '@/lib/api/omni-search';
import { savedFilterPageLabel } from './saved-filter-page-label';
import type { OmniEntityKind } from './types';
import { currentTaskPrefix, getCurrentProject } from '@/lib/current-project';
import { projectPath, taskPath } from '@/navigation/project-paths';

/** Search runs inside a project (the top bar shows it only there), so results link into it. */
function currentBoard(): string {
  const slug = getCurrentProject()?.slug;
  return slug ? projectPath(slug) : '/projects';
}

function currentTaskPath(taskId: string): string {
  const slug = getCurrentProject()?.slug;
  return slug ? taskPath(slug, taskId) : '/projects';
}

export type EntitySearchSource = {
  kind: OmniEntityKind;
  heading: string;
  icon: LucideIcon;
  listPath: string;
  search: (q: string, limit: number) => Promise<unknown[]>;
  getId: (row: unknown) => string;
  getLabel: (row: unknown) => string;
  href: (row: unknown) => string;
  renderOption: (row: unknown) => ReactNode;
};

function asTask(row: unknown): TaskSearchHit {
  return row as TaskSearchHit;
}
function asSavedFilter(row: unknown): SavedFilterSearchItem {
  return row as SavedFilterSearchItem;
}

function savedFilterHref(row: SavedFilterSearchItem): string {
  try {
    return buildSavedFilterHref(row.pathname, row.queryString);
  } catch (err) {
    if (err instanceof SavedFilterNormalizationError) {
      return row.pathname;
    }
    throw err;
  }
}

export const ENTITY_REGISTRY: Record<OmniEntityKind, EntitySearchSource> = {
  tasks: {
    kind: 'tasks',
    heading: 'Tasks',
    icon: SquareKanban,
    get listPath() {
      return currentBoard();
    },
    search: (q, limit) => searchTasks(q, limit),
    getId: (row) => asTask(row).id,
    getLabel: (row) => asTask(row).title,
    href: (row) => currentTaskPath(asTask(row).id),
    renderOption: (row) => {
      const t = asTask(row);
      const status = t.archived ? 'Archived' : t.column_name;
      return (
        <div className={t.archived || t.is_done ? 'min-w-0 opacity-70' : 'min-w-0'}>
          <div className="truncate font-medium">{t.title}</div>
          <div className="truncate text-xs text-muted-foreground">
            {[formatTaskNumber(t.number, currentTaskPrefix()), status, t.context_title].filter(Boolean).join(' · ')}
          </div>
        </div>
      );
    },
  },
  saved_filters: {
    kind: 'saved_filters',
    heading: 'Saved views',
    icon: Bookmark,
    get listPath() {
      return currentBoard();
    },
    search: (q, limit) => searchSavedFilters(q, limit, { inProject: Boolean(getCurrentProject()) }),
    getId: (row) => asSavedFilter(row).id,
    getLabel: (row) => {
      const f = asSavedFilter(row);
      return `${f.name} · ${savedFilterPageLabel(f.pathname)}`;
    },
    href: (row) => savedFilterHref(asSavedFilter(row)),
    renderOption: (row) => {
      const f = asSavedFilter(row);
      const page = savedFilterPageLabel(f.pathname);
      const hasFilters = Boolean(f.queryString?.trim());
      return (
        <div className="min-w-0">
          <div className="truncate font-medium">{f.name}</div>
          <div className="truncate text-xs text-muted-foreground">
            {page}
            {f.personal ? ' · every project, yours only' : ''}
            {hasFilters ? ' · with filters' : ''}
          </div>
        </div>
      );
    },
  },
};

export const DEFAULT_ENTITY_KINDS: OmniEntityKind[] = ['tasks', 'saved_filters'];
